import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageSquare, CheckCircle, Star, TrendingUp, Download, Eye, Search, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import useFeedbackManagementStore from '@/stores/AdminStore/FeedbackManagementStore';
import { getFormResponsesPaginated, PaginatedResponsesData } from '@/services/PagesServices/FeedbackManagementService';
import LoadingSpinner from '@/Components/common/LoadingSpinner';
import ErrorDisplay from '@/Components/common/ErrorDisplay';
import './ResponseAnalytics.css';

const ResponseAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const rolePrefix = pathname.startsWith('/superadmin') ? '/superadmin' : '/admin';
  const {
    analyticsStats,
    barChartData,
    ratingDistribution,
    surveyOptions,
    selectedFormId,
    setSelectedFormId,
    loading,
    error,
  } = useFeedbackManagementStore();

  const [responsesData, setResponsesData] = useState<PaginatedResponsesData | null>(null);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Searchable dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [surveySearch, setSurveySearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Helper function to get icon component based on icon name
  const getIcon = (iconName: string): React.ReactElement => {
    const icons: { [key: string]: React.ReactElement } = {
      'MessageSquare': <MessageSquare size={20} />,
      'CheckCircle': <CheckCircle size={20} />,
      'Star': <Star size={20} />,
      'TrendingUp': <TrendingUp size={20} />,
    };
    return icons[iconName] || <MessageSquare size={20} />;
  };

  const handleViewResponse = (responseId: number) => {
    navigate(`${rolePrefix}/tools/feedback/response/${responseId}`);
  };

  // Fetch paginated responses when form, page, or search term changes
  useEffect(() => {
    const fetchResponses = async () => {
      if (!selectedFormId) {
        setResponsesData(null);
        return;
      }

      setLoadingResponses(true);
      try {
        const result = await getFormResponsesPaginated(
          selectedFormId,
          currentPage,
          20,
          searchTerm
        );

        if (result.success && result.data) {
          setResponsesData(result.data);
        } else {
          setResponsesData(null);
        }
      } catch (error) {
        console.error('Error fetching paginated responses:', error);
        setResponsesData(null);
      } finally {
        setLoadingResponses(false);
      }
    };

    fetchResponses();
  }, [selectedFormId, currentPage, searchTerm]);

  // Handle search with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1); // Reset to first page on new search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Filter survey options based on search
  const filteredSurveyOptions = surveyOptions?.filter(option =>
    option.label.toLowerCase().includes(surveySearch.toLowerCase())
  ) || [];

  // Get selected survey name
  const selectedSurveyName = surveyOptions?.find(opt => opt.value === selectedFormId)?.label || '';

  // Handle survey selection
  const handleSurveySelect = (surveyId: string) => {
    setSelectedFormId(surveyId);
    setDropdownOpen(false);
    setSurveySearch('');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="admin-feedback-response-analytics">
      {!selectedFormId && !loading && (
        <div className="admin-feedback-survey-selector-centered">
          <div className="admin-feedback-selector-wrapper" ref={dropdownRef}>
            <div className="admin-feedback-searchable-dropdown">
              <div
                className="admin-feedback-dropdown-trigger"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <Search size={18} className="admin-feedback-dropdown-search-icon" />
                <input
                  type="text"
                  className="admin-feedback-dropdown-input"
                  placeholder="Search and select a survey..."
                  value={surveySearch}
                  onChange={(e) => {
                    setSurveySearch(e.target.value);
                    setDropdownOpen(true);
                  }}
                  onFocus={() => setDropdownOpen(true)}
                />
                <ChevronDown size={20} className={`admin-feedback-dropdown-chevron ${dropdownOpen ? 'open' : ''}`} />
              </div>

              {dropdownOpen && (
                <div className="admin-feedback-dropdown-menu">
                  {filteredSurveyOptions.length > 0 ? (
                    filteredSurveyOptions.map((option) => (
                      <div
                        key={option.id}
                        className="admin-feedback-dropdown-item"
                        onClick={() => handleSurveySelect(option.value)}
                      >
                        <span>{option.label}</span>
                      </div>
                    ))
                  ) : (
                    <div className="admin-feedback-dropdown-empty">
                      No surveys found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <h4>Select a Survey to View Analytics</h4>
          <p>Choose a survey from the dropdown above to view detailed response analytics</p>
        </div>
      )}

      {loading && (
        <LoadingSpinner size="lg" message="Loading analytics..." />
      )}

      {error && !loading && (
        <ErrorDisplay
          title="Failed to load analytics"
          message={error}
          type="error"
          showRetry={true}
          onRetry={() => window.location.reload()}
        />
      )}

      {selectedFormId && !loading && !error && (
        <>
          <div className="admin-feedback-selected-survey-bar">
            <div className="admin-feedback-selected-survey-info" ref={dropdownRef}>
              <div className="admin-feedback-searchable-dropdown compact">
                <div
                  className="admin-feedback-dropdown-trigger compact"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  <span className="admin-feedback-selected-survey-text">{selectedSurveyName}</span>
                  <ChevronDown size={18} className={`admin-feedback-dropdown-chevron ${dropdownOpen ? 'open' : ''}`} />
                </div>

                {dropdownOpen && (
                  <div className="admin-feedback-dropdown-menu compact">
                    <div className="admin-feedback-dropdown-search-wrapper">
                      <Search size={16} className="admin-feedback-dropdown-search-icon-menu" />
                      <input
                        type="text"
                        className="admin-feedback-dropdown-search-input"
                        placeholder="Search surveys..."
                        value={surveySearch}
                        onChange={(e) => setSurveySearch(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="admin-feedback-dropdown-items">
                      {filteredSurveyOptions.length > 0 ? (
                        filteredSurveyOptions.map((option) => (
                          <div
                            key={option.id}
                            className={`admin-feedback-dropdown-item ${option.value === selectedFormId ? 'selected' : ''}`}
                            onClick={() => handleSurveySelect(option.value)}
                          >
                            <span>{option.label}</span>
                          </div>
                        ))
                      ) : (
                        <div className="admin-feedback-dropdown-empty">
                          No surveys found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <button className="admin-feedback-export-btn">
              <Download size={16} />
              Export
            </button>
          </div>

          <div className="admin-feedback-stats-grid">
            {analyticsStats?.map((stat, index) => (
              <div key={index} className="admin-feedback-stat-card">
                <div className="admin-feedback-stat-icon" style={{ backgroundColor: `${stat.color}20`, color: stat.color }}>
                  {getIcon(stat.icon)}
                </div>
                <div className="admin-feedback-stat-info">
                  <p className="admin-feedback-stat-label">{stat.label}</p>
                  <h3 className="admin-feedback-stat-value">{stat.value}</h3>
                  <p className="admin-feedback-stat-subtext">{stat.subtext}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="admin-feedback-analytics-charts">
        <div className="admin-feedback-chart-section admin-feedback-bar-chart-section">
          <h4>Average Rating By Question</h4>
          <p className="admin-feedback-chart-subtitle">Monthly feedback submissions</p>

          <div className="admin-feedback-bar-chart">
            <div className="admin-feedback-chart-y-axis">
              <span>5</span>
              <span>4</span>
              <span>3</span>
              <span>2</span>
              <span>1</span>
            </div>
            <div className="admin-feedback-chart-bars">
              {barChartData?.map((item, index) => (
                <div key={index} className="admin-feedback-bar-item">
                  <div className="admin-feedback-bar-container">
                    <div className="admin-feedback-bar" style={{ height: `${item.height}%` }}>
                      <span className="admin-feedback-bar-value">{item.value}</span>
                    </div>
                  </div>
                  <div className="admin-feedback-bar-label">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="admin-feedback-chart-section admin-feedback-pie-chart-section">
          <h4>Rating Distribution</h4>
          <p className="admin-feedback-chart-subtitle">Survey feedback ratings breakdown</p>

          <div className="admin-feedback-pie-chart-container">
            <svg viewBox="0 0 200 200" className="admin-feedback-pie-chart">
              {ratingDistribution && (() => {
                let cumulativePercentage = 0;
                const centerX = 100;
                const centerY = 100;
                const radius = 80;

                const getCoordinatesForPercent = (percent: number) => {
                  const x = centerX + radius * Math.cos(2 * Math.PI * percent);
                  const y = centerY + radius * Math.sin(2 * Math.PI * percent);
                  return { x, y };
                };

                return ratingDistribution.map((slice, index) => {
                  const startPercentage = cumulativePercentage;
                  cumulativePercentage += slice.percentage / 100;
                  const endPercentage = cumulativePercentage;

                  const start = getCoordinatesForPercent(startPercentage - 0.25);
                  const end = getCoordinatesForPercent(endPercentage - 0.25);

                  const largeArcFlag = slice.percentage > 50 ? 1 : 0;

                  const pathData = [
                    `M ${centerX} ${centerY}`,
                    `L ${start.x} ${start.y}`,
                    `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
                    'Z'
                  ].join(' ');

                  return (
                    <path
                      key={index}
                      d={pathData}
                      fill={slice.color}
                    />
                  );
                });
              })()}
              <circle cx="100" cy="100" r="45" fill="var(--color-greyish-bg)" />
              {ratingDistribution && ratingDistribution.length > 0 && (() => {
                const topRating = ratingDistribution.reduce((max, r) => r.percentage > max.percentage ? r : max, ratingDistribution[0]);
                return (
                  <>
                    <text x="100" y="95" textAnchor="middle" fontSize="28" fontWeight="bold" fill="var(--color-black-text)">{topRating.percentage}%</text>
                    <text x="100" y="115" textAnchor="middle" fontSize="12" fill="var(--color-secondary-text)">{topRating.stars} Stars</text>
                  </>
                );
              })()}
            </svg>

            <div className="admin-feedback-rating-legend">
              {ratingDistribution?.map((item) => (
                <div key={item.stars} className="admin-feedback-legend-item">
                  <div className="admin-feedback-legend-left">
                    <div className="admin-feedback-legend-color" style={{ backgroundColor: item.color }} />
                    <span className="admin-feedback-legend-label">{item.stars} Stars:</span>
                  </div>
                  <span className="admin-feedback-legend-value">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="admin-feedback-recent-responses">
        <div className="admin-feedback-responses-header">
          <h4>Recent Responses</h4>
          <div className="admin-feedback-search-bar">
            <Search size={18} className="admin-feedback-search-icon" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="admin-feedback-search-input"
            />
          </div>
        </div>

        {loadingResponses && (
          <LoadingSpinner size="md" message="Loading responses..." />
        )}

        {!loadingResponses && responsesData && responsesData.responses.length === 0 && (
          <div className="admin-feedback-no-responses">
            <MessageSquare size={48} />
            <h4>No Responses Found</h4>
            <p>{searchTerm ? 'Try adjusting your search' : 'No responses yet for this survey'}</p>
          </div>
        )}

        {!loadingResponses && responsesData && responsesData.responses.length > 0 && (
          <>
            <div className="admin-feedback-responses-table-container">
              <table className="admin-feedback-responses-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Submission Time</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {responsesData.responses.map((response) => (
                    <tr key={response.id}>
                      <td>{response.respondent_name || 'Anonymous'}</td>
                      <td>{response.respondent_email || 'N/A'}</td>
                      <td>{formatDate(response.submitted_at)}</td>
                      <td>
                        <button
                          className="admin-feedback-view-response-btn"
                          onClick={() => handleViewResponse(response.id)}
                          title="View full response"
                        >
                          <Eye size={16} />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {responsesData.pagination.totalPages > 1 && (
              <div className="admin-feedback-pagination">
                <button
                  className="admin-feedback-pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>

                <div className="admin-feedback-pagination-pages">
                  {Array.from({ length: responsesData.pagination.totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      className={`admin-feedback-pagination-page ${currentPage === page ? 'active' : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="admin-feedback-pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === responsesData.pagination.totalPages}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default ResponseAnalytics;
